import { Suspense, lazy } from "react";
import type { RouteObject } from "react-router";

import {
  type DashboardAgentsDetailsQuery,
  type DashboardAgentsDocumentsDetailsQuery,
} from "@app_schema";

import type { Breadcrumb } from "@application/types/breadcrumb";

import { DASHBOARD_AGENTS_DETAILS_LOADER } from "@application/loaders/dashboard_agents_details_loader";
import { DASHBOARD_AGENTS_DOCUMENTS_DETAILS_LOADER } from "@application/loaders/dashboard_agents_documents_details_loader";
import { DASHBOARD_AGENTS_EDIT_LOADER } from "@application/loaders/dashboard_agents_edit_loader";
import { DASHBOARD_AGENTS_LIST_LOADER } from "@application/loaders/dashboard_agents_list_loader";
import { DASHBOARD_AGENTS_NEW_LOADER } from "@application/loaders/dashboard_agents_new_loader";

import { DASHBOARD_AGENTS_LINK } from "@application/navigation/dashboard_agents_link";

import { Loader } from "@styled/loader";

const DashboardAgentsDetails = lazy(() =>
  import("./agents_details").then((m) => ({
    default: m.DashboardAgentsDetails,
  })),
);
const DashboardAgentsDocumentsDetails = lazy(() =>
  import("./agents_documents_details").then((m) => ({
    default: m.DashboardAgentsDocumentsDetails,
  })),
);
const DashboardAgentsDocumentsList = lazy(() =>
  import("./agents_documents_list").then((m) => ({
    default: m.DashboardAgentsDocumentsList,
  })),
);
const DashboardAgentsEdit = lazy(() =>
  import("./agents_edit").then((m) => ({ default: m.DashboardAgentsEdit })),
);
const DashboardAgentsList = lazy(() =>
  import("./agents_list").then((m) => ({ default: m.DashboardAgentsList })),
);
const DashboardAgentsNew = lazy(() =>
  import("./agents_new").then((m) => ({ default: m.DashboardAgentsNew })),
);

export const DASHBOARD_AGENTS_ROUTE = {
  path: "agents",
  handle: DASHBOARD_AGENTS_LINK,
  children: [
    {
      index: true,
      element: (
        <Suspense fallback={<Loader />}>
          <DashboardAgentsList />
        </Suspense>
      ),
      loader: DASHBOARD_AGENTS_LIST_LOADER,
    },
    {
      path: "new",
      element: (
        <Suspense fallback={<Loader />}>
          <DashboardAgentsNew />
        </Suspense>
      ),
      loader: DASHBOARD_AGENTS_NEW_LOADER,
      handle: {
        name: "New",
        to: "/dashboard/agents/new",
      },
    },
    {
      path: ":id",
      loader: DASHBOARD_AGENTS_DETAILS_LOADER,
      handle: (data: DashboardAgentsDetailsQuery): Breadcrumb => ({
        name: data.agent.name,
        to: `/dashboard/agents/${data.agent.id}`,
      }),
      children: [
        {
          index: true,
          element: (
            <Suspense fallback={<Loader />}>
              <DashboardAgentsDetails />
            </Suspense>
          ),
          loader: DASHBOARD_AGENTS_DETAILS_LOADER,
        },
        {
          path: "edit",
          element: (
            <Suspense fallback={<Loader />}>
              <DashboardAgentsEdit />
            </Suspense>
          ),
          loader: DASHBOARD_AGENTS_EDIT_LOADER,
          handle: (data: DashboardAgentsDetailsQuery): Breadcrumb => ({
            name: "Edit",
            to: `/dashboard/agents/${data.agent.id}/edit`,
          }),
        },
        {
          path: "documents",
          loader: DASHBOARD_AGENTS_DETAILS_LOADER,
          handle: (data: DashboardAgentsDetailsQuery): Breadcrumb => ({
            name: "Documents",
            to: `/dashboard/agents/${data.agent.id}/documents`,
          }),
          children: [
            {
              index: true,
              element: (
                <Suspense fallback={<Loader />}>
                  <DashboardAgentsDocumentsList />
                </Suspense>
              ),
            },
            {
              path: ":documentID",
              element: (
                <Suspense fallback={<Loader />}>
                  <DashboardAgentsDocumentsDetails />
                </Suspense>
              ),
              loader: DASHBOARD_AGENTS_DOCUMENTS_DETAILS_LOADER,
              handle: (
                data: DashboardAgentsDocumentsDetailsQuery,
              ): Breadcrumb => ({
                name: data.agent.document.name,
                to: `/dashboard/agents/${data.agent.id}/documents/${data.agent.document.id}`,
              }),
            },
          ],
        },
      ],
    },
  ],
} satisfies RouteObject;
